/* ════════════════════════════════════════════════════════════════
   ComicScene — the hero's 80s comic-cover backdrop, all in three.js:
   a blue-washed shader "panel" with two-tone sunburst rays radiating
   from behind the name and CMYK-style halftone screens at rotated
   print angles (a blue screen over most of the panel, the accent
   screen toward the inked edges). Colors are pulled from the site's
   CSS variables, so it follows the theme.
   ════════════════════════════════════════════════════════════════ */
import { useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

/* ── halftone + speed-line panel ── */
const vert = /* glsl */`
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
const frag = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  uniform float uTime;
  uniform vec2  uRes;
  uniform vec3  uBg;    // panel center
  uniform vec3  uBg2;   // panel edge (darker)
  uniform vec3  uDot;   // accent halftone screen
  uniform vec3  uBlue;  // comic blue wash + blue screen

  vec2 rot(vec2 p, float a) {
    float c = cos(a), s = sin(a);
    return mat2(c, -s, s, c) * p;
  }

  void main() {
    vec2 uv = vUv;
    vec2 p = uv - 0.5;
    p.x *= uRes.x / uRes.y;             // aspect-correct
    float d = length(p);

    // ease off the blue ink on a light theme so the pale copy keeps
    // its contrast; full strength on the dark navy theme
    float lum = dot(uBg, vec3(0.299, 0.587, 0.114));
    float soften = 1.0 - smoothstep(0.45, 0.85, lum) * 0.45;

    // blue-washed radial gradient: comic blue toward the middle,
    // deep navy at the panel edges
    vec3 mid = mix(uBg, uBlue, 0.38 * soften);
    vec3 col = mix(mid, uBg2, clamp(d * 1.2, 0.0, 1.0));

    // two-tone sunburst wedges — the classic 80s cover backdrop of
    // alternating rays radiating from behind the hero
    float ang = atan(p.y, p.x) + uTime * 0.03;
    float wedge = step(0.5, fract(ang * 9.0 / 3.14159));   // 18 rays
    float wedgeMask = smoothstep(0.16, 0.55, d);
    vec3 rayCol = mix(uBg, uBlue, 0.6 * soften);
    col = mix(col, rayCol, wedge * wedgeMask * 0.32);

    // CMYK-style halftone screens at rotated print angles, like the
    // four-color process of an 80s comic page
    vec2 px = uv * uRes;

    // blue screen at ~15deg, covering most of the panel
    vec2 pb = rot(px, 0.2618);
    vec2 gvB = fract(pb / 11.0) - 0.5;
    float distB = length(gvB) * 11.0;
    float toneB = smoothstep(0.12, 0.95, d);
    float radB = mix(0.7, 3.3, toneB);
    float dotB = smoothstep(radB + 1.0, radB - 1.0, distB);
    col = mix(col, uBlue, dotB * 0.4 * soften);

    // accent screen at 45deg, growing toward the inked edges
    vec2 pa = rot(px, 0.7854);
    vec2 gvA = fract(pa / 14.0) - 0.5;
    float distA = length(gvA) * 14.0;
    float toneA = smoothstep(0.35, 1.15, d);
    float radA = mix(0.3, 4.0, toneA);
    float dotA = smoothstep(radA + 1.0, radA - 1.0, distA);
    col = mix(col, uDot, dotA * 0.42);

    gl_FragColor = vec4(col, 1.0);
  }
`;

function readColor(el, name, fallback) {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return new THREE.Color(v || fallback);
}

function Panel() {
  const { viewport, size, gl } = useThree();
  const mat = useRef();
  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uRes:  { value: new THREE.Vector2(1, 1) },
    uBg:   { value: new THREE.Color('#163563') },
    uBg2:  { value: new THREE.Color('#0d2247') },
    uDot:  { value: new THREE.Color('#4cf06f') },
    uBlue: { value: new THREE.Color('#5b9bff') },
  }), []);

  useFrame((state) => {
    const u = mat.current?.uniforms;
    if (!u) return;
    u.uTime.value = state.clock.elapsedTime;
    u.uRes.value.set(size.width, size.height);
    // follow the theme (cheap enough at 60fps for a couple of vars)
    const el = gl.domElement;
    u.uBg.value.copy(readColor(el, '--bg-soft', '#163563'));
    u.uBg2.value.copy(readColor(el, '--bg', '#0d2247'));
    u.uDot.value.copy(readColor(el, '--accent', '#4cf06f'));
    u.uBlue.value.copy(readColor(el, '--blue', '#5b9bff'));
  });

  return (
    <mesh position={[0, 0, -2]} scale={[viewport.width * 1.6, viewport.height * 1.6, 1]}>
      <planeGeometry args={[1, 1]} />
      <shaderMaterial ref={mat} vertexShader={vert} fragmentShader={frag} uniforms={uniforms} depthWrite={false} />
    </mesh>
  );
}

export default function ComicScene() {
  return (
    <Canvas
      className="comic-scene-canvas"
      dpr={[1, 1.8]}
      gl={{ antialias: true }}
      camera={{ position: [0, 0, 8], fov: 45 }}
    >
      <Panel />
    </Canvas>
  );
}
